/**
 * Canonical TanStack Query key factory (E10-F01-T04).
 *
 * One place that owns every query key, so a cache invalidation after a mutation
 * targets exactly the right entries and can never drift from the key a list
 * actually used. Keys are hierarchical (`['projects']` is a prefix of
 * `['projects','list',params]` and `['projects','detail',id]`), which lets a
 * mutation invalidate a whole domain with a single prefix
 * (`queryClient.invalidateQueries({ queryKey: qk.projects.all })`).
 *
 * Covered list domains (acceptance criterion: "кэширует списки
 * projects/clients/emails"):
 *   - projects  → GET /projects
 *   - clients   → GET /clients   (Address Book)
 *   - emails    → GET /emails    (Inbox)
 */

/** Arbitrary, serialisable list parameters (filters, search, pagination). */
export type ListParams = Readonly<Record<string, string | number | boolean | undefined>>;

function domain(name: string) {
  return {
    /** Prefix key — invalidates every query under this domain. */
    all: [name] as const,
    /** All list queries for the domain (any params). */
    lists: () => [name, 'list'] as const,
    /** A specific list query, keyed by its params. */
    list: (params?: ListParams) => [name, 'list', params ?? {}] as const,
    /** All detail queries for the domain. */
    details: () => [name, 'detail'] as const,
    /** A specific entity detail, keyed by id. */
    detail: (id: string | number) => [name, 'detail', id] as const,
  };
}

/** The query-key registry. Import as `qk` across feature hooks. */
export const qk = {
  projects: domain('projects'),
  clients: domain('clients'),
  emails: domain('emails'),
} as const;

export type QueryDomain = keyof typeof qk;
